<script setup lang="ts">
import { ElDialog } from 'element-plus'

/**
 * The "are you sure?" dialog itself. **A slice never renders this** — it asks through
 * `useConfirm()`, and `ConfirmDialogHost` is what draws this in answer.
 *
 * It is worded for a loss by default: a red "Confirm". A question that is not a loss passes
 * `{ confirmLabel, tone: 'primary' }` to `confirm()`.
 */
withDefaults(
  defineProps<{
    message: string
    confirmLabel?: string
    tone?: 'danger' | 'primary'
  }>(),
  { confirmLabel: 'Confirm', tone: 'danger' }
)

const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'cancel'): void
}>()

/**
 * Escape is the third way out, beside Cancel and Confirm, and it has to answer the promise
 * like the other two. A dialog that merely hides itself leaves `useConfirm`'s state set, so
 * the host's `v-if` never flips back and every later question on the page resolves
 * to nothing.
 */
const handleBeforeClose = () => emit('cancel')
</script>

<template>
  <ElDialog
    :model-value="true"
    width="400px"
    :close-on-click-modal="false"
    :show-close="false"
    :before-close="handleBeforeClose"
  >
    <span>{{ message }}</span>

    <template #footer>
      <el-button @click="$emit('cancel')"> Cancel </el-button>

      <el-button :type="tone" @click="$emit('confirm')">
        {{ confirmLabel }}
      </el-button>
    </template>
  </ElDialog>
</template>
